import React, { useState } from 'react'
import { updatePreferences } from '../api'
import Button from './Button'
import Input from './Input'

/**
 * Ask for the client's EIN AT THE POINT WHERE IT UNLOCKS SOMETHING.
 *
 * This is the direct answer to ADR-0012's revisit note: `clients.org_ein` is
 * optional, "almost nobody fills it in unprompted — yet every differentiated
 * feature keys on it", and it "should be asked for at the point where it
 * unlocks something visible, not buried in preferences".
 *
 * Preferences already has an EIN field and it has produced ZERO real EINs — as
 * of 2026-08-12 no client on the system has one, so `already_funded` has never
 * fired and the co-funder graph has never served a real session. A field on a
 * settings page competes with fourteen other fields and explains itself in a
 * hint nobody reads. This component instead appears inside the empty space
 * where the answer WOULD have been, so the ask and the payoff are the same
 * rectangle.
 *
 * Three rules it follows, and they are the whole design:
 *
 * 1. **Name the payoff, not the field.** The CALLER writes the sentence above
 *    this form and passes the verb through `cta` — "Show me", not "Save". The
 *    prompt is never generic ("complete your profile"), because the reason to
 *    hand over an EIN is always a specific thing the reader wanted to know.
 * 2. **Never imply the answer is no.** The surrounding copy says we could not
 *    LOOK. An EIN prompt rendered where "no results" would go is the exact
 *    misreading this whole feature has to avoid.
 * 3. **Stay optional and stay dismissible-feeling.** Onboarding is held to
 *    "under 5 minutes / minimum viable profile" and the EIN is not a matching
 *    input, so nothing here may read as a requirement. There is no skip button
 *    because there is nothing to skip — the page works without it.
 *
 * On success it calls `onSaved` so the caller can re-fetch and show the real
 * answer in place. Seeing the result appear where the form was is the entire
 * argument for asking here rather than in Preferences.
 */
export default function EinPrompt({
  onSaved,
  cta = 'Show me',
  label = 'Add your EIN',
}) {
  const [value, setValue] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  async function submit(e) {
    e.preventDefault()
    if (saving) return
    setError('')
    setSaving(true)
    try {
      await updatePreferences({ org_ein: value })
      // The parent re-fetches. Deliberately NOT clearing `saving` first: the
      // button stays busy until the answer it promised is on screen.
      await onSaved?.()
    } catch (err) {
      // The backend's message is the useful one ("An EIN is 9 digits, like
      // 04-2121342…") — it already explains the format and that blank is fine.
      setError(err.message || 'Could not save that EIN.')
      setSaving(false)
    }
  }

  return (
    <form onSubmit={submit} className="mt-4 max-w-md mx-auto text-left">
      {/* The placeholder is a FORMAT, never a realistic value. The one client
          EIN that ever reached prod was `04-2121342` — verbatim the example
          this field used to show. A format cannot be copied as data, and if
          someone pastes it anyway `format_ein` rejects it rather than storing
          it silently. Same rule in Preferences and in the API error messages. */}
      <div className="flex flex-col sm:flex-row sm:items-start gap-2">
        <Input
          label={label}
          className="flex-1"
          value={value}
          onChange={e => setValue(e.target.value)}
          placeholder="NN-NNNNNNN"
          inputMode="numeric"
          autoComplete="off"
          error={error}
          hint="Nine digits, from the top of your Form 990. We never show it to anyone."
        />
        {/* Aligned to the input, not the label above it. */}
        <div className="sm:pt-6">
          <Button type="submit" loading={saving} disabled={!value.trim()}>
            {cta}
          </Button>
        </div>
      </div>
    </form>
  )
}
